// Install offer state for the web app (TRD-41). No React here: the browser's install event can
// arrive before the first render, so it is caught at boot and handed to the UI through subscribe().

const DISMISSED_AT = "pwa:installDismissedAt";
const DISMISS_COUNT = "pwa:installDismissCount";
const INSTALLED = "pwa:installed";

const DAY_MS = 86400000;

// first "later" waits a week, the second a month, and a third means they do not want it
export const COOLDOWN_MS = [7 * DAY_MS, 30 * DAY_MS];
export const MAX_DISMISSALS = 3;

// Safari 17 on Sonoma is the first that can add a site to the Dock
const MIN_SAFARI_DOCK_VERSION = 17;

// held for the life of the page: Chrome fires the event once and prompt() can be called only once
let deferredPrompt = null;
let version = 0;
const listeners = new Set();

const notify = () => {
    version += 1;
    listeners.forEach((listener) => listener());
};

export const subscribe = (listener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

// a number that moves on every change, so a hook can tell when to read again
export const getVersion = () => version;

export const hasDeferredPrompt = () => !!deferredPrompt;

const readNumber = (key) => {
    try {
        return Number(localStorage.getItem(key)) || 0;
    } catch (error) {
        return 0;
    }
};

// storage can be unwritable in a private window; the offer then just repeats, which is harmless
const write = (key, value) => {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, String(value));
    } catch (ignore) {}
};

export const readInstallRecord = () => ({
    dismissedAt: readNumber(DISMISSED_AT),
    dismissCount: readNumber(DISMISS_COUNT),
    installed: (() => {
        try {
            return localStorage.getItem(INSTALLED) === "yes";
        } catch (error) {
            return false;
        }
    })(),
});

export const markInstalled = () => {
    write(INSTALLED, "yes");
    notify();
};

export const recordDismiss = (now = Date.now()) => {
    write(DISMISSED_AT, now);
    write(DISMISS_COUNT, readInstallRecord().dismissCount + 1);
    notify();
};

// Chrome only offers an install for an app that is not installed, so the event itself proves an
// earlier install was removed, and the stale flag must not keep hiding the offer
const onBeforeInstallPrompt = (event) => {
    event.preventDefault();
    deferredPrompt = event;
    write(INSTALLED, null);
    notify();
};

const onAppInstalled = () => {
    deferredPrompt = null;
    markInstalled();
};

let captured = false;

/** Call once, before React mounts. Returns a teardown, for tests. */
export const captureInstallPrompt = (win = window) => {
    if (captured || !win?.addEventListener) return () => {};

    captured = true;
    win.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    win.addEventListener("appinstalled", onAppInstalled);

    return () => {
        win.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        win.removeEventListener("appinstalled", onAppInstalled);
        captured = false;
        deferredPrompt = null;
    };
};

/** True inside the installed app, where an offer to install it makes no sense. */
export const isStandalone = (win = window) => {
    try {
        const mode = (query) => !!win.matchMedia?.(`(display-mode: ${query})`)?.matches;

        return mode("standalone") || mode("window-controls-overlay") || mode("minimal-ui") || win.navigator?.standalone === true;
    } catch (error) {
        return false;
    }
};

/**
 * "native": the browser handed us its install prompt.
 * "safari-mac": no prompt exists, but File > Add to Dock does, so a guide can help.
 * "unsupported": nothing to offer. Phones and tablets are served by the mobile site.
 */
export const getInstallPlatform = ({hasManifest, hasPrompt, browserName, browserVersion, isMacOs, isMobile}) => {
    // without this brand's manifest, Safari would put a generic name and icon in the Dock
    if (!hasManifest || isMobile) return "unsupported";
    if (hasPrompt) return "native";
    if (isMacOs && browserName === "Safari" && Number.parseInt(browserVersion, 10) >= MIN_SAFARI_DOCK_VERSION) {
        return "safari-mac";
    }

    return "unsupported";
};

export const cooldownMsFor = (dismissCount) => COOLDOWN_MS[Math.min(dismissCount, COOLDOWN_MS.length) - 1] || 0;

export const shouldOfferInstall = ({platform, standalone, record, now = Date.now()}) => {
    if (platform === "unsupported" || standalone) return false;
    if (record.installed) return false;
    if (record.dismissCount >= MAX_DISMISSALS) return false;
    if (!record.dismissCount) return true;

    return now - record.dismissedAt >= cooldownMsFor(record.dismissCount);
};

/** Shows the browser's own dialog. Resolves "accepted", "dismissed" or "unavailable". */
export const promptInstall = async (now = Date.now()) => {
    const event = deferredPrompt;

    if (!event) return "unavailable";

    try {
        await event.prompt();
        const {outcome} = await event.userChoice;

        if (outcome === "accepted") markInstalled();
        else recordDismiss(now);

        return outcome;
    } catch (error) {
        return "unavailable";
    } finally {
        // one shot: a used event cannot prompt again, so drop it whatever the answer
        if (deferredPrompt === event) deferredPrompt = null;
        notify();
    }
};
